<template>
  <page>
    <v-card>
      <v-card-title>
        <v-text-field append-icon="mdi-magnify" label="Search" single-line hide-details v-model="search"></v-text-field>
        <v-spacer></v-spacer>
        <v-btn color="primary" dark class=" mr-2" @click="doNew">
          New Abbreviated Bank Name
          <v-icon right>mdi-plus</v-icon>
        </v-btn>
        <v-flex xs12 class="mt-3">
          <message :messages="messages"></message>
        </v-flex>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="items"
        :search="search"
        :footer-props="{'items-per-page-options': itemsPerPage }"
        v-bind:options.sync="pagination"
        item-key="name"
        :loading="loading"
        :no-data-text="noDataText"
        :custom-filter="customSearch"
      >
        <template v-slot:item="row"
          ><tr>
            <td class="text-xs-left">{{ row.item.bankAbbrev }}</td>
            <td class="text-xs-left">{{ row.item.bankDesc }}</td>
            <td class="table-action-header">
              <table-action
                :rowData="row"
                review
                @review="doReview(row.item.bankAbbrev)"
                query
                @query="doQuery(row.item.bankAbbrev)"
                remove
                @remove="doDelete(row.item.bankAbbrev)"
              ></table-action>
            </td></tr
        ></template>
      </v-data-table>
    </v-card>
    <v-dialog v-model="bankAbbrevReview" width="800px" persistent>
      <v-card>
        <v-toolbar v-if="reviewMode" dense color="grey lighten-3" flat>
          <v-icon>mdi-pencil</v-icon>
          <v-toolbar-title>Edit Abbreviated Bank Name</v-toolbar-title>
        </v-toolbar>
        <v-toolbar v-if="queryMode" dense color="grey lighten-3" flat>
          <v-icon>mdi-magnify</v-icon>
          <v-toolbar-title>Query Abbreviated Bank Name</v-toolbar-title>
        </v-toolbar>
        <v-toolbar v-if="newBankAbbrev" dense color="grey lighten-3" flat>
          <v-icon>mdi-plus</v-icon>
          <v-toolbar-title>Create New Abbreviated Bank Name</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <message :messages="messages"></message>
          <v-container grid-list-md>
            <v-layout wrap>
              <v-flex xs6 v-if="newBankAbbrev">
                <v-text-field
                  :label="labels.bankAbbrev"
                  v-model="bankAbbrevForm.bankAbbrev"
                  :error-messages="errors.collect('bankAbbrev')"
                  data-vv-name="bankAbbrev"
                  :data-vv-as="labels.bankAbbrev"
                  v-validate="'required|param|max:8'"
                ></v-text-field>
              </v-flex>
              <v-flex xs6 v-else>
                <v-subheader class="queryLabel">{{ labels.bankAbbrev }}</v-subheader>
                <div class="queryValue">{{ bankAbbrevForm.bankAbbrev }}</div>
              </v-flex>
              <v-flex xs6>
                <v-text-field
                  :disabled="queryMode"
                  :label="labels.bankDesc"
                  v-model="bankAbbrevForm.bankDesc"
                  :error-messages="errors.collect('bankDesc')"
                  data-vv-name="bankDesc"
                  :data-vv-as="labels.bankDesc"
                  v-validate="'required|entry|max:120'"
                ></v-text-field>
              </v-flex>
            </v-layout>
            <v-layout wrap>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.createdBy }}</v-subheader>
                <div class="queryValue">{{ bankAbbrevForm.createdBy }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.createdDate }}</v-subheader>
                <div class="queryValue">{{ bankAbbrevForm.createdDate | formatDate }}</div>
              </v-flex>
            </v-layout>
            <v-layout wrap v-if="!newBankAbbrev">
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.modifiedBy }}</v-subheader>
                <div class="queryValue">{{ bankAbbrevForm.modifiedBy }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.modifiedDate }}</v-subheader>
                <div class="queryValue">{{ bankAbbrevForm.modifiedDate | formatDate }}</div>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red darken-1" text @click.native="doClose">Cancel</v-btn>
          <v-btn color="primary" text @click.native="doSave">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <confirm ref="confirmDelete">
      <p>Are you sure you want to delete the selected Abbreviated Bank Name? ({{ selectedBankAbbrev }})</p>
    </confirm>
  </page>
</template>

<script>
import * as TableConst from '@/mixins/table'
import MessageMixin from '@/mixins/message'
import { expandURI } from '@/utils/ajax'
import { formatDisplayDate } from '@/utils/date'
export default {
  data() {
    return {
      headers: [
        { text: 'Abbreviation', value: 'bankAbbrev', align: 'left' },
        { text: 'Bank Name', value: 'bankDesc', align: 'left' },
        TableConst.tableActionHeader
      ],
      labels: {
        bankAbbrev: 'Abbreviation',
        bankDesc: 'Bank Name',
        createdBy: 'Created By',
        createdDate: 'Created Date',
        modifiedBy: 'Modified By',
        modifiedDate: 'Modified Date'
      },
      bankAbbrevForm: {
        bankAbbrev: null,
        bankDesc: null,
        createdBy: null,
        createdDate: null,
        modifiedBy: null,
        modifiedDate: null
      },
      newBankAbbrev: false,
      bankAbbrevReview: false,
      selectedBankAbbrev: null,
      queryMode: false,
      reviewMode: false,
      pagination: {
        sortDesc: [false],
        page: 1
      }
    }
  },
  created() {
    this.doInit()
  },
  methods: {
    doInit() {
      const apiURI = this.menu.bankAbbrevList
      if (apiURI) {
        this.loading = true
        this.$http
          .get(apiURI)
          .then((result) => {
            this.items = []
            const { data } = result.data
            if (data) {
              data.map((bankAbbrev) => {
                const convertList = {
                  bankAbbrev: bankAbbrev.bankAbbrev,
                  bankDesc: bankAbbrev.bankDesc
                }
                this.items.push(convertList)
              })
            }
            this.loading = false
          })
          .catch((err) => {
            this.loading = false
            const { data } = err.response
            if (data) {
              this.message(data)
              this.fieldMessage(data)
            }
          })
      }
    },
    doNew() {
      this.bankAbbrevForm.bankAbbrev = null
      this.bankAbbrevForm.bankDesc = null
      this.bankAbbrevForm.createdBy = this.$store.state.token.firstName + ' ' + this.$store.state.token.lastName
      this.bankAbbrevForm.createdDate = new Date()
      this.bankAbbrevReview = true
      this.newBankAbbrev = true
    },
    async doReview(bankAbbrev) {
      this.doFetchData(bankAbbrev)
      this.reviewMode = true
      this.queryMode = false
    },
    async doQuery(bankAbbrev) {
      this.doFetchData(bankAbbrev)
      this.reviewMode = false
      this.queryMode = true
    },
    doFetchData(bankAbbrev) {
      this.resetValidator()
      this.resetMessages()
      if (bankAbbrev) {
        this.newFund = false
        const apiURI = this.menu.bankAbbrev
        if (apiURI) {
          this.$http
            .get(expandURI(apiURI, { bankAbbrev }))
            .then((result) => {
              const { data } = result.data
              if (data) {
                this.bankAbbrevForm.bankAbbrev = data.bankAbbrev.bankAbbrev
                this.bankAbbrevForm.bankDesc = data.bankAbbrev.bankDesc
                this.bankAbbrevForm.createdBy = data.createdBy
                this.bankAbbrevForm.createdDate = data.createdDate
                this.bankAbbrevForm.modifiedBy = data.modifiedBy
                this.bankAbbrevForm.modifiedDate = data.modifiedDate
                this.bankAbbrevReview = true
                this.newBankAbbrev = false
              }
            })
            .catch((err) => {
              const { data } = err.response
              if (data) {
                this.message(data)
                this.fieldMessage(data)
              }
            })
        }
      }
    },
    doSave() {
      this.resetMessages()
      this.$validator.validateAll().then((result) => {
        if (result) {
          const config = {
            method: this.newBankAbbrev ? 'post' : 'put',
            url: this.newBankAbbrev ? this.menu.createBankAbbrev : this.menu.updateBankAbbrev,
            data: this.bankAbbrevForm
          }
          if (config.url) {
            this.$http
              .request(config)
              .then((result) => {
                const { data } = result
                this.notify(data)
                this.doClose()
                this.doInit()
              })
              .catch((err) => {
                const { data } = err.response
                if (data) {
                  this.message(data)
                  this.fieldMessage(data)
                }
              })
          }
        } else {
          this.promptFieldCheck()
        }
      })
    },
    async doDelete(bankAbbrev) {
      this.resetMessages()
      if (bankAbbrev) {
        const apiURI = this.menu.checkDeleteBankAbbrev
        const apiURI2 = this.menu.deleteBankAbbrev
        if (apiURI && apiURI2) {
          try {
            await this.$http.get(expandURI(apiURI, { bankAbbrev }))
            this.selectedBankAbbrev = bankAbbrev
            const prompt = await this.$refs.confirmDelete.doPopUp()
            if (prompt) {
              const result = await this.$http.delete(expandURI(apiURI2, { bankAbbrev }))
              const { data } = result
              this.notify(data)
              this.doInit()
            }
          } catch (err) {
            const { data } = err.response
            if (data) {
              this.notify(data)
            }
          }
        }
      }
    },
    doClose() {
      this.resetMessages()
      this.bankAbbrevReview = false
      this.newBankAbbrev = false
    }
  },
  computed: {
    menu() {
      return this.$store.state.bankParam.menu
    }
  },
  filters: {
    formatDate(value) {
      if (value) {
        return formatDisplayDate(value)
      } else {
        return null
      }
    }
  },
  mixins: [TableConst.default, MessageMixin]
}
</script>

<style>
</style>
